import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Plus, Pencil, Globe, Lock, Copy, Archive, Send } from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn } from '@ui-admin/components/DataGrid/types';
import type { ReportDefinitionLocal } from '@firesquirrel/local-db';
import {
  archiveReportDefinition,
  duplicateReportDefinition,
  listReportDefinitions,
  publishReportDefinition,
  toggleLibrary,
} from '../../services/systemReportDefinitionService';

const columns: DataGridColumn<ReportDefinitionLocal>[] = [
  {
    id: 'name',
    header: 'Report Name',
    accessorKey: 'name',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: (info) => {
      const row = info.row.original;
      const statusColors: Record<string, string> = {
        draft: 'bg-gray-100 text-gray-800',
        published: 'bg-green-100 text-green-800',
        archived: 'bg-yellow-100 text-yellow-800',
      };
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            statusColors[row.status] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Unknown'}
        </span>
      );
    },
  },
  {
    id: 'library',
    header: 'Library',
    cell: (info) => {
      const row = info.row.original;
      if (row.scope?.level !== 'system') return <span className="text-gray-300">—</span>;
      return row.isInLibrary
        ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><Globe className="h-3.5 w-3.5" /> In Library</span>
        : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3.5 w-3.5" /> Not in Library</span>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: (info) => new Date(info.row.original.updatedAt).toLocaleDateString(),
  },
];

export default function SystemDataReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportDefinitionLocal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
  }, []);

  async function loadReports() {
    setLoading(true);
    try {
      const data = await listReportDefinitions({
        type: 'report',
        scopeLevel: 'system',
      });
      setReports(data);
    } catch (error) {
      console.error('[SystemDataReportsPage] Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleRefresh = async () => {
    await loadReports();
  };

  if (loading) return null;

  return (
    <>
    <GridPageShell
      gridId="system-data-reports"
      gridType="data-reports"
      title="System Data Reports"
      subtitle={`${reports.length} ${reports.length === 1 ? 'system report' : 'system reports'} ready for authoring and library curation`}
      columns={columns}
      data={reports}
      getRowId={(row) => row._id}
      searchPlaceholder="Search system data reports..."
      toolbarActions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/data-report-templates/new')}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            New System Report
          </button>
        </div>
      }
      onRefresh={handleRefresh}
      emptyIcon={BarChart3}
      emptyTitle="No system data reports"
      emptySubtitle="Create your first system-owned report, then publish it to add it to the library."
      emptyCTA={{ label: 'New System Report', onClick: () => navigate('/data-report-templates/new') }}
      onRowClick={(row) => navigate(`/data-report-templates/${row._id}`)}
      rowActions={[
        {
          id: 'edit',
          label: 'Edit',
          icon: Pencil,
          onClick: (row) => navigate(`/data-report-templates/${row._id}`),
        },
        {
          id: 'duplicate',
          label: 'Duplicate',
          icon: Copy,
          onClick: async (row) => {
            await duplicateReportDefinition(row._id);
            await loadReports();
          },
        },
        {
          id: 'publish',
          label: 'Publish',
          icon: Send,
          onClick: async (row) => {
            await publishReportDefinition(row._id);
            await loadReports();
          },
          hidden: (row) => row.status === 'published' || row.status === 'archived',
        },
        {
          id: 'archive',
          label: 'Archive',
          icon: Archive,
          onClick: async (row) => {
            await archiveReportDefinition(row._id);
            await loadReports();
          },
          hidden: (row) => row.status === 'archived',
        },
        {
          id: 'addToLibrary',
          label: 'Add to Library',
          icon: Globe,
          onClick: async (row) => {
            await toggleLibrary(row._id, true);
            await loadReports();
          },
          hidden: (row) => row.scope?.level !== 'system' || row.status !== 'published' || !!row.isInLibrary,
        },
        {
          id: 'removeFromLibrary',
          label: 'Remove from Library',
          icon: Lock,
          onClick: async (row) => {
            await toggleLibrary(row._id, false);
            await loadReports();
          },
          hidden: (row) => row.scope?.level !== 'system' || !row.isInLibrary,
        },
      ]}
    />
  </>
  );
}
